import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Radio, Users, Gem, DollarSign, ArrowDownToLine, Zap, Video, PenSquare,
  Image as ImageIcon, Calendar, ArrowRight, TrendingUp, Coins,
} from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";

/**
 * Overview — Streamer command-center home.
 * Aggregates live status, financials, and recent activity into a single
 * dense-but-readable snapshot. All calls tolerate 404/403 so partially-set-up
 * accounts still render a useful page.
 */
export default function Overview() {
  const { user } = useAuth();
  const [channel, setChannel] = useState(null);
  const [earnings, setEarnings] = useState(null);
  const [tips, setTips] = useState([]);
  const [subs, setSubs] = useState([]);
  const [followers, setFollowers] = useState(0);

  useEffect(() => {
    if (!user) return;
    api.get(`/channels/${user.username}`).then((r) => setChannel(r.data)).catch(() => {});
    api.get("/earnings/summary").then((r) => setEarnings(r.data)).catch(() => {});
    api.get("/tips/received").then((r) => setTips(r.data.tips || [])).catch(() => {});
    api.get(`/users/${user.username}/followers/count`).then((r) => setFollowers(r.data?.count ?? 0)).catch(() => {});
    // Latest channel subs — the streamer's own list of subscribers.
    api.get(`/channels/${user.username}/subscribers`).then((r) => setSubs(r.data?.subscribers || [])).catch(() => {});
  }, [user?.username]);

  const isLive = !!channel?.is_live;
  const viewers = channel?.viewer_count || 0;
  const monthlyRevenue = (earnings?.lifetime_earned_cents || 0) / 100;
  const pendingPayout = (earnings?.available_cents || 0) / 100;

  return (
    <div className="space-y-6" data-testid="section-overview">
      {/* Status hero */}
      <div className="term-card p-5 flex flex-wrap items-center gap-4">
        <div
          className={`w-12 h-12 rounded-full grid place-items-center ${isLive ? "" : "bg-zinc-900"}`}
          style={isLive ? { background: "var(--sc-pink)22", border: "1px solid var(--sc-pink)" } : undefined}
        >
          <Radio className={`w-5 h-5 ${isLive ? "animate-pulse" : ""}`} style={{ color: isLive ? "var(--sc-pink)" : "rgb(113 113 122)" }} />
        </div>
        <div className="min-w-0 flex-1">
          <div className="sc-label text-[10px]" style={{ color: isLive ? "var(--sc-pink)" : "rgb(113 113 122)" }}>
            {isLive ? "◉ LIVE NOW" : "OFFLINE"}
          </div>
          <div className="sc-heading text-lg font-black truncate">{channel?.title || "No stream title set"}</div>
          <div className="text-xs text-zinc-500 truncate">{channel?.category || "—"}</div>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Link to={`/c/${user.username}`} className="sc-btn-ghost text-xs flex items-center gap-1.5" data-testid="overview-community-btn">
            <Users className="w-3.5 h-3.5" /> Community
          </Link>
          <Link to={`/c/${user.username}/live?golive=1`} className="sc-btn text-xs flex items-center gap-1.5" data-testid="overview-go-live">
            <Zap className="w-3.5 h-3.5" /> Go Live
          </Link>
        </div>
      </div>

      {/* Stat grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <StatCard icon={Users}          color="cyan"   label="VIEWERS"         value={viewers.toLocaleString()}                      hint={isLive ? "Right now" : "Offline"} testid="overview-stat-viewers" />
        <StatCard icon={Users}          color="pink"   label="FOLLOWERS"       value={followers.toLocaleString()}                    hint="All-time"        testid="overview-stat-followers" />
        <StatCard icon={Gem}            color="volt"   label="SUBSCRIBERS"     value={(subs?.length || 0).toLocaleString()}          hint="Active"          testid="overview-stat-subs" />
        <StatCard icon={Coins}          color="orange" label="GOLD COINS"      value={(user.gold_coins || 0).toLocaleString()}       hint="On-hand"         testid="overview-stat-gc" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        <StatCard icon={TrendingUp}     color="volt"   label="LIFETIME EARNED" value={`$${monthlyRevenue.toFixed(2)}`}                hint="Your cut, all-time" big testid="overview-stat-lifetime" />
        <StatCard icon={ArrowDownToLine} color="pink"  label="PENDING PAYOUT"  value={`$${pendingPayout.toFixed(2)}`}                 hint={earnings?.can_withdraw ? "Ready to withdraw" : `Min $${(earnings?.min_withdrawal_usd ?? 10).toFixed(2)}`} big testid="overview-stat-payout" cta={{ to: "/dashboard/payments", label: "Withdraw" }} />
      </div>

      {/* Quick actions */}
      <div>
        <div className="sc-label mb-2" style={{ color: "var(--sc-volt)" }}>◤ QUICK ACTIONS</div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <QuickAction icon={Zap}         to={`/c/${user.username}/live?golive=1`}  label="Go Live"           color="pink"   testid="overview-qa-golive" />
          <QuickAction icon={PenSquare}   to="/dashboard/channel"              label="Edit Stream"       color="cyan"   testid="overview-qa-edit" />
          <QuickAction icon={ImageIcon}   to="/dashboard/channel#banner"       label="Update Banner"     color="volt"   testid="overview-qa-banner" />
          <QuickAction icon={Calendar}    to="/dashboard/channel#schedule"     label="Schedule Stream"   color="orange" testid="overview-qa-schedule" />
        </div>
      </div>

      {/* Recent activity */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        <RecentList
          title="Latest Tips"
          testid="overview-latest-tips"
          empty="No tips yet — share your stream link to get started."
          items={(tips || []).slice(0, 5).map((t) => ({
            key: t.id,
            left: t.from_username,
            right: `${t.amount} GC`,
            sub: new Date(t.created_at).toLocaleString(),
          }))}
        />
        <RecentList
          title="Latest Subscribers"
          testid="overview-latest-subs"
          empty="No subscribers yet — set your sub price in Subscriptions."
          items={(subs || []).slice(0, 5).map((s) => ({
            key: s.subscriber_username || s.user_id,
            left: s.subscriber_username || s.username,
            right: s.source === "stripe" ? "Stripe" : "Gold Coins",
            sub: s.started_at ? `Since ${new Date(s.started_at).toLocaleDateString()}` : "",
          }))}
        />
      </div>
    </div>
  );
}

function StatCard({ icon: Icon, color, label, value, hint, big, testid, cta }) {
  return (
    <div className={`term-card ${color} p-4 ${big ? "lg:p-5" : ""}`} data-testid={testid}>
      <div className="flex items-center gap-2 mb-2">
        <Icon className="w-3.5 h-3.5" style={{ color: `var(--sc-${color})` }} />
        <span className="sc-label text-[10px]" style={{ color: `var(--sc-${color})` }}>{label}</span>
      </div>
      <div className={`sc-heading font-black tracking-tight ${big ? "text-3xl" : "text-2xl"}`}>{value}</div>
      <div className="flex items-center justify-between mt-1">
        <span className="text-[10px] text-zinc-500">{hint}</span>
        {cta && (
          <Link to={cta.to} className="text-[10px] sc-mono text-[color:var(--sc-cyan)] hover:underline flex items-center gap-0.5">
            {cta.label} <ArrowRight className="w-2.5 h-2.5" />
          </Link>
        )}
      </div>
    </div>
  );
}

function QuickAction({ icon: Icon, to, label, color, testid }) {
  return (
    <Link
      to={to}
      className="term-card p-4 hover:bg-white/[0.03] transition flex items-center gap-3 group"
      data-testid={testid}
    >
      <div className="w-9 h-9 rounded grid place-items-center" style={{ background: `var(--sc-${color})15`, border: `1px solid var(--sc-${color})30` }}>
        <Icon className="w-4 h-4" style={{ color: `var(--sc-${color})` }} />
      </div>
      <span className="text-sm font-bold text-zinc-200 group-hover:text-white">{label}</span>
      <ArrowRight className="w-4 h-4 ml-auto text-zinc-600 group-hover:text-zinc-300 transition" />
    </Link>
  );
}

function RecentList({ title, items, empty, testid }) {
  return (
    <div className="term-card p-4" data-testid={testid}>
      <div className="sc-label mb-3" style={{ color: "var(--sc-cyan)" }}>◈ {title.toUpperCase()}</div>
      {items.length === 0 ? (
        <div className="text-xs text-zinc-500 py-6 text-center">{empty}</div>
      ) : (
        <ul className="space-y-2">
          {items.map((i) => (
            <li key={i.key} className="flex items-center justify-between p-2 rounded hover:bg-white/5">
              <div className="min-w-0">
                <div className="text-sm text-zinc-200 truncate">{i.left}</div>
                {i.sub && <div className="text-[10px] sc-mono text-zinc-500">{i.sub}</div>}
              </div>
              <div className="sc-mono text-sm font-bold text-[color:var(--sc-volt)] ml-3 shrink-0">{i.right}</div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
